import { useState } from 'react';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { WEDGE, DASHED } from '@moleculeone/draw';

import { styled } from '../../../../stitches.config';
import { BondDashed, BondWedge } from './BondChiral';
import { BondLine } from './BondLine';
import { M4, V4 } from '@moleculeone/utils';

export const CUT_APPLIED = 'CUT_APPLIED';
export const CUT_DESIRED = 'CUT_DESIRED';

const SVGGroup = styled('g', {
  'userSelect': 'none',
  '&:hover .bondBackground': {
    opacity: 1,
  },
  '&:hover .disconnectionBackground': {
    stroke: '#ddd',
  },
});

const defaultCutProps = () => ({
  type: CUT_APPLIED,
  bgColor: 'white',
  bgOpacity: 1,
  color: 'red',
});

export function Bond(props: any) {
  // eslint-disable-next-line no-unused-vars
  const { bond, cutProps, title, color = 'black', warning = 'false', pos1, pos2, ...restProps } = props;

  const [lifetimeId] = useState<string>(uuidv4());

  const renderBond = (pos1: any, pos2: any, key: any, data: any, warning: any, single = false) => {
    const decorationProps = {
      pos1,
      pos2,
      backgroundClassName: 'bondBackground',
      bgColor: warning ? 'red' : '#ddd',
      bgOpacity: warning ? 0.5 : 0,
      color,
      single,
    };

    switch (data.type) {
      case WEDGE:
        return <BondWedge key={key} {...decorationProps} />;
      case DASHED:
        return <BondDashed key={key} {...decorationProps} />;
      default:
        return <BondLine key={key} {...decorationProps} />;
    }
  };

  const renderCut = (pos1: any, pos2: any, key: any) => {
    const { backgroundClassName, ...restCutProps } = cutProps;

    const lineProps = {
      'backgroundClassName': cx('disconnectionBackground', backgroundClassName),
      pos1,
      pos2,
      'data-test': `svg-bond-${key}-${lifetimeId}`,
      ...defaultCutProps(),
      ...restCutProps,
    };

    return <BondLine key={key} {...lineProps} />;
  };

  const single = !bond.left && !bond.right;
  const result = [renderBond(pos1, pos2, 'center', bond.center, warning, single)];

  const midPos = V4.average(pos1, pos2);
  const transform = M4.mulM(
    M4.moveTo(midPos),
    M4.scaleS(Math.sqrt(3) / 2),
    M4.rotZ(Math.PI * 0.5),
    M4.moveFrom(midPos),
  );

  const leftSide = M4.mulV(transform, pos1);
  const rightSide = M4.mulV(transform, pos2);

  if (bond.left) {
    const shift1 = V4.normal(V4.sub(leftSide, pos1), (35 * Math.sqrt(2)) / 2);
    const shift2 = V4.normal(V4.sub(leftSide, pos2), (35 * Math.sqrt(2)) / 2);
    const left1 = V4.add(pos1, shift1);
    const left2 = V4.add(pos2, shift2);
    result.push(renderBond(left1, left2, 'left', bond.left, warning));
  }

  if (bond.right) {
    const shift1 = V4.normal(V4.sub(rightSide, pos1), (35 * Math.sqrt(2)) / 2);
    const shift2 = V4.normal(V4.sub(rightSide, pos2), (35 * Math.sqrt(2)) / 2);
    const right1 = V4.add(pos1, shift1);
    const right2 = V4.add(pos2, shift2);
    result.push(renderBond(right1, right2, 'right', bond.right, warning));
  }

  if (cutProps) {
    const right = V4.move(rightSide, V4.sub(rightSide, leftSide), 0.2);
    const left = V4.move(leftSide, V4.sub(leftSide, rightSide), 0.2);
    result.push(renderCut(right, left, 'cut'));
  }

  return (
    <SVGGroup {...restProps} className={cx(restProps.className, 'bond-group')}>
      {result}
      <title>{title}</title>
    </SVGGroup>
  );
}
